/**
 * Verify whether an email is valid.
 *
 * An email is considered valid iff:
 * - It contains a non-empty sequence before the '@' sign.
 * - It contains an '@' sign.
 * - It contains a non-empty sequence after the '@' sign and before a '.' mark.
 * - It contains a non-empty sequence after the '.' mark.
 *
 * @param email The email to verify
 * @return True if the email is valid, false otherwise.
 **/
export function isEmailValid(email: string): boolean {
	const regex =
		/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
	return regex.test(email);
}

/**
 * Verify whether a password is valid.
 *
 * A password is valid iff:
 * - It is not empty.
 *
 * @param password The password to verify.
 * @return True if the password is valid, false otherwise.
 **/
export function isPasswordValid(password: string): boolean {
	if (password.length < 8) {
		return false;
	}

	const upperCase = /[A-Z]/;
	const lowerCase = /[a-z]/;
	const number = /\d/;
	const special = /[+\-=*/\\!@#$%^&(){}[\]|_'",.;:~]/;

	const histogram = [0, 0, 0, 0, 0];
	for (const element of password) {
		const character = element;

		if (upperCase.test(character)) {
			++histogram[0];
		} else if (lowerCase.test(character)) {
			++histogram[1];
		} else if (number.test(character)) {
			++histogram[2];
		} else if (special.test(character)) {
			++histogram[3];
		} else {
			++histogram[4];
		}
	}

	return histogram.filter(c => c > 0).length >= 2;
}

/**
 * Convert the value to a title.
 * This means that the first letter is capitalized.
 *
 * @param value the original value.
 * @return The title version of the string.
 **/
export function asTitle(value: string): string {
	return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Tests whether a value is null or empty.
 *
 * @param value The value to check.
 * @return True if the value is empty or null
 **/
export function isEmpty(value: string): boolean {
	return value.length === 0;
}

/**
 * Delete an element from a list.
 * If the index is negative our out of bounds, no changes are applied.
 *
 * @param index The index of the element to delete. (0 < index <= list.length)
 * @param list The original list to delete the element from.
 * @return A new version of the list with the specified element removed.
 **/
export function deleteElement<T>(index: number, list: T[]): T[] {
	if (index < 0 || index >= list.length) {
		return list;
	}

	return list.slice(0, index).concat(list.slice(index + 1));
}

/**
 * Delete a list of elements from a list.
 *
 * @param index The indices of the elements to delete.
 * @param list The original list to delete the elements from.
 * @return A new version of the list with the specified elements removed.
 **/
export function deleteElements<T>(indices: number[], list: T[]): T[] {
	// TODO: can be optimized by first splitting all, and then concatting?
	const sortedIndices = [...indices].sort((a, b) => b - a);
	for (const index of sortedIndices) {
		list = deleteElement(index, list);
	}
	return list;
}

/**
 * Insert an element to a list.
 * If the index is negative our out of bounds, no changes are applied.
 *
 * @param index The index where to insert the element. (0 < index < list.length)
 * @param element The element to insert.
 * @param list The original list to which to add the element.
 * @return A new version of the list with the specified element added.
 **/
export function insertElement<T>(index: number, element: T, list: T[]): T[] {
	if (index < 0 || index > list.length) {
		return list;
	}

	return list.slice(0, index).concat(element).concat(list.slice(index));
}

/**
 * Swap an old element for a new element at a different location.
 *
 * @param oldIndex The index of the element to remove.
 * @param newIndex The index where to add the new element.
 * @param element The new element to be added.
 * @param list The original list on which to perform the operations.
 * @return A new version of the list with the element swapped.
 **/
export function swapElement<T>(
	oldIndex: number,
	newIndex: number,
	element: T,
	list: T[]
): T[] {
	if (
		oldIndex < 0 ||
		oldIndex >= list.length ||
		newIndex < 0 ||
		newIndex >= list.length
	) {
		return list;
	}

	let result = list;
	result = deleteElement(oldIndex, result);
	result = insertElement(newIndex, element, result);
	return result;
}

/**
 * Replace an element with another one.
 *
 * @param index The location of the element.
 * @param element The new element to be put in its place.
 * @param list The original list on which to perform the operartions.
 * @Return A new version of the list with the element replaced.
 */
export function replaceElement<T>(index: number, element: T, list: T[]): T[] {
	return swapElement(index, index, element, list);
}

/**
 * Flips the element in te list.
 * Remove the element if it already is in the list, add it if not.
 *
 * @param element The element to be flipped.
 * @param list The list in which to do it.
 * @Return A new version of the list with the element flipped.
 */
export function flipElement<T>(element: T, list: T[]): T[] {
	const index = list.indexOf(element);
	if (index !== -1) {
		return deleteElement(index, list);
	} else {
		return list.concat(element);
	}
}

/**
 * Flips the element in te list.
 * Remove the element if it already is in the list, add it if not.
 *
 * @param include Should the element to included in the list or not.
 * @param element The element to be flipped.
 * @param list The list in which to do it.
 * @Return A new version of the list with the element flipped.
 */
export function setElement<T>(include: boolean, element: T, list: T[]): T[] {
	const index = list.indexOf(element);
	if (index !== -1 && !include) {
		return deleteElement(index, list);
	} else if (index === -1 && include) {
		return list.concat(element);
	}

	return list;
}
